import * as React from "react";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { DayButton, DayPicker, getDefaultClassNames } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/ui/button";

/**
 * Calendar Component
 *
 * A calendar component for date selection and display.
 * Built on top of react-day-picker with shadcn/ui styling.
 *
 * @example
 * ```tsx
 * <Calendar
 *   mode="single"
 *   selected={date}
 *   onSelect={setDate}
 *   className="rounded-md border"
 * />
 * ```
 */
function Calendar({
    className,
    classNames,
    showOutsideDays = true,
    captionLayout = "dropdown",
    buttonVariant = "ghost",
    formatters,
    components,
    ...props
}: React.ComponentProps<typeof DayPicker> & {
    buttonVariant?: React.ComponentProps<typeof Button>["variant"];
}) {
    const defaultClassNames = getDefaultClassNames();

    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            className={cn(
                "setu:bg-background setu:group/calendar setu:p-3 setu:[--cell-size:--spacing(8)] setu:[[data-slot=card-content]_&]:bg-transparent setu:[[data-slot=popover-content]_&]:bg-transparent",
                String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
                String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
                className
            )}
            captionLayout={captionLayout}
            formatters={{
                formatMonthDropdown: (date) => date.toLocaleString("default", { month: "short" }),
                formatYearDropdown: (date) => date.getFullYear().toString(),
                ...formatters,
            }}
            classNames={{
                root: cn("setu:w-fit", defaultClassNames.root),
                months: cn(
                    "setu:flex setu:gap-4 setu:flex-col setu:md:flex-row setu:relative",
                    defaultClassNames.months
                ),
                month: cn("setu:flex setu:flex-col setu:w-full setu:gap-4", defaultClassNames.month),
                nav: cn(
                    "setu:flex setu:items-center setu:gap-1 setu:w-full setu:absolute setu:top-0 setu:inset-x-0 setu:justify-between",
                    defaultClassNames.nav
                ),
                button_previous: cn(
                    buttonVariants({ variant: buttonVariant }),
                    "setu:size-(--cell-size) setu:aria-disabled:opacity-50 setu:p-0 setu:select-none",
                    defaultClassNames.button_previous
                ),
                button_next: cn(
                    buttonVariants({ variant: buttonVariant }),
                    "setu:size-(--cell-size) setu:aria-disabled:opacity-50 setu:p-0 setu:select-none",
                    defaultClassNames.button_next
                ),
                month_caption: cn(
                    "setu:flex setu:items-center setu:justify-center setu:h-(--cell-size) setu:w-full setu:px-(--cell-size)",
                    defaultClassNames.month_caption
                ),
                dropdowns: cn(
                    "setu:w-full setu:flex setu:items-center setu:text-sm setu:font-medium setu:justify-center setu:h-(--cell-size) setu:gap-1.5",
                    defaultClassNames.dropdowns
                ),
                dropdown_root: cn(
                    "setu:relative setu:has-focus:!border-ring setu:!border setu:!border-input setu:shadow-xs setu:has-focus:ring-ring/50 setu:has-focus:ring-[3px] setu:rounded-md",
                    defaultClassNames.dropdown_root
                ),
                dropdown: cn("setu:absolute setu:bg-popover setu:inset-0 setu:opacity-0", defaultClassNames.dropdown),
                caption_label: cn(
                    "setu:select-none setu:font-medium",
                    captionLayout === "label"
                        ? "setu:text-sm"
                        : "setu:rounded-md setu:pl-2 setu:pr-1 setu:flex setu:items-center setu:gap-1 setu:text-sm setu:h-8 setu:[&>svg]:text-muted-foreground setu:[&>svg]:size-3.5",
                    defaultClassNames.caption_label
                ),
                table: "setu:w-full setu:!border-collapse",
                weekdays: cn("flex", defaultClassNames.weekdays),
                weekday: cn(
                    "setu:text-muted-foreground setu:rounded-md setu:flex-1 setu:font-normal setu:text-[0.8rem] setu:select-none",
                    defaultClassNames.weekday
                ),
                week: cn("setu:flex setu:w-full setu:mt-2", defaultClassNames.week),
                week_number_header: cn("setu:select-none setu:w-(--cell-size)", defaultClassNames.week_number_header),
                week_number: cn(
                    "setu:text-[0.8rem] setu:select-none setu:text-muted-foreground",
                    defaultClassNames.week_number
                ),
                day: cn(
                    "setu:relative setu:w-full setu:h-full setu:p-0 setu:text-center setu:[&:first-child[data-selected=true]_button]:rounded-l-md setu:[&:last-child[data-selected=true]_button]:rounded-r-md setu:group/day setu:aspect-square setu:select-none",
                    defaultClassNames.day
                ),
                range_start: cn("setu:rounded-l-md setu:bg-accent", defaultClassNames.range_start),
                range_middle: cn("setu:rounded-none", defaultClassNames.range_middle),
                range_end: cn("rounded-r-md bg-accent", defaultClassNames.range_end),
                today: cn(
                    "setu:bg-accent setu:text-accent-foreground setu:rounded-md setu:data-[selected=true]:rounded-none",
                    defaultClassNames.today
                ),
                outside: cn(
                    "setu:text-muted-foreground setu:aria-selected:text-muted-foreground",
                    defaultClassNames.outside
                ),
                disabled: cn("setu:text-muted-foreground setu:opacity-50", defaultClassNames.disabled),
                hidden: cn("invisible", defaultClassNames.hidden),
                ...classNames,
            }}
            components={{
                Root: ({ className, rootRef, ...props }) => {
                    return <div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />;
                },
                Chevron: ({ className, orientation, ...props }) => {
                    if (orientation === "left") {
                        return <ChevronLeftIcon className={cn("setu:size-4", className)} {...props} />;
                    }

                    if (orientation === "right") {
                        return <ChevronRightIcon className={cn("setu:size-4", className)} {...props} />;
                    }

                    return <ChevronDownIcon className={cn("setu:size-4", className)} {...props} />;
                },
                DayButton: CalendarDayButton,
                WeekNumber: ({ children, ...props }) => {
                    return (
                        <td {...props}>
                            <div className="setu:flex setu:size-(--cell-size) setu:items-center setu:justify-center setu:text-center">
                                {children}
                            </div>
                        </td>
                    );
                },
                ...components,
            }}
            {...props}
        />
    );
}
Calendar.displayName = "Calendar";

/**
 * CalendarDayButton Component
 *
 * Custom day button component for the calendar.
 */
function CalendarDayButton({ className, day, modifiers, ...props }: React.ComponentProps<typeof DayButton>) {
    const defaultClassNames = getDefaultClassNames();

    const ref = React.useRef<HTMLButtonElement>(null);
    React.useEffect(() => {
        if (modifiers.focused) ref.current?.focus();
    }, [modifiers.focused]);

    return (
        <Button
            ref={ref}
            variant="ghost"
            size="icon"
            data-day={day.date.toLocaleDateString()}
            data-selected-single={
                modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle
            }
            data-range-start={modifiers.range_start}
            data-range-end={modifiers.range_end}
            data-range-middle={modifiers.range_middle}
            className={cn(
                "setu:data-[selected-single=true]:bg-primary setu:data-[selected-single=true]:text-primary-foreground setu:data-[range-middle=true]:bg-accent setu:data-[range-middle=true]:text-accent-foreground setu:data-[range-start=true]:bg-primary setu:data-[range-start=true]:text-primary-foreground setu:data-[range-end=true]:bg-primary setu:data-[range-end=true]:text-primary-foreground setu:group-data-[focused=true]/day:!border-ring setu:group-data-[focused=true]/day:ring-ring/50 setu:dark:hover:text-accent-foreground setu:flex setu:aspect-square setu:size-auto setu:w-full setu:min-w-(--cell-size) setu:flex-col setu:gap-1 setu:leading-none setu:font-normal setu:group-data-[focused=true]/day:relative setu:group-data-[focused=true]/day:z-10 setu:group-data-[focused=true]/day:ring-[3px] setu:data-[range-end=true]:rounded-md setu:data-[range-end=true]:rounded-r-md setu:data-[range-middle=true]:rounded-none setu:data-[range-start=true]:rounded-md setu:data-[range-start=true]:rounded-l-md setu:[&>span]:text-xs setu:[&>span]:opacity-70",
                defaultClassNames.day,
                className
            )}
            {...props}
        />
    );
}
CalendarDayButton.displayName = "CalendarDayButton";

export { Calendar, CalendarDayButton };
